import { PopularBetsPodTypes } from '@frontend/sports/types/components/popular-bets';

import { IPodHeightConfigModel } from './popular-bets.models';

const DEFAULT_POD_WIDTH = 284;
const DEFAULT_POD_LIST_GAP = 8;

interface PodHeights {
    HEADER: number;
    SUMMARY_TEXT: number;
    SUMMARY_TEXT_WITH_ODDS: number;
    LEG: number;
    LEG_WITH_ODDS: number;
    GAP_BETWEEN_LEG: number;
    GAP_BETWEEN_LEG_ODDS: number;
    ADD_TO_BETSLIP: number;
}

type PodHeightsCollection = {
    [key in PopularBetsPodTypes]: PodHeights;
};

const POD_HEIGHTS_DEFAULT: PodHeightsCollection = {
    [PopularBetsPodTypes.Parlay]: {
        HEADER: 21,
        SUMMARY_TEXT: 42,
        SUMMARY_TEXT_WITH_ODDS: 55,
        LEG_WITH_ODDS: 55,
        LEG: 42,
        GAP_BETWEEN_LEG: 0,
        GAP_BETWEEN_LEG_ODDS: 0,
        ADD_TO_BETSLIP: 55,
    },
    [PopularBetsPodTypes.SGP]: {
        HEADER: 36,
        SUMMARY_TEXT: 28,
        SUMMARY_TEXT_WITH_ODDS: 28,
        LEG: 28,
        LEG_WITH_ODDS: 55,
        GAP_BETWEEN_LEG: 8,
        GAP_BETWEEN_LEG_ODDS: 8,
        ADD_TO_BETSLIP: 56,
    },
    [PopularBetsPodTypes.SGPPlus]: {
        HEADER: 36,
        SUMMARY_TEXT: 28,
        SUMMARY_TEXT_WITH_ODDS: 28,
        LEG: 28,
        LEG_WITH_ODDS: 55,
        GAP_BETWEEN_LEG: 8,
        GAP_BETWEEN_LEG_ODDS: 8,
        ADD_TO_BETSLIP: 56,
    },
};

const POD_HEIGHTS_EPCOT_PARLAY: PodHeights = {
    HEADER: 44,
    SUMMARY_TEXT: 50,
    SUMMARY_TEXT_WITH_ODDS: 50,
    LEG_WITH_ODDS: 36,
    LEG: 26,
    GAP_BETWEEN_LEG: 10,
    GAP_BETWEEN_LEG_ODDS: 12,
    ADD_TO_BETSLIP: 44,
};

const POD_HEIGHTS_EPCOT: PodHeightsCollection = {
    [PopularBetsPodTypes.Parlay]: POD_HEIGHTS_EPCOT_PARLAY,
    [PopularBetsPodTypes.SGP]: POD_HEIGHTS_EPCOT_PARLAY,
    [PopularBetsPodTypes.SGPPlus]: POD_HEIGHTS_EPCOT_PARLAY,
};

function getPodHeights(config: IPodHeightConfigModel): PodHeights {
    const { isEpcotEnabled, podType, isAllPodAreSameType, showOptionPick, isAnyPodHasSummary } = config;

    const podHeightsConfig = isEpcotEnabled ? POD_HEIGHTS_EPCOT : POD_HEIGHTS_DEFAULT;
    const selectedPodHeights = isAllPodAreSameType
        ? { ...podHeightsConfig[podType] }
        : { ...podHeightsConfig[podType], HEADER: Math.max(podHeightsConfig.SGP.HEADER, podHeightsConfig.Parlay.HEADER) };

    if (podType === PopularBetsPodTypes.Parlay && showOptionPick) {
        selectedPodHeights.LEG = selectedPodHeights.LEG_WITH_ODDS;
        selectedPodHeights.SUMMARY_TEXT = selectedPodHeights.SUMMARY_TEXT_WITH_ODDS;
        selectedPodHeights.GAP_BETWEEN_LEG = selectedPodHeights.GAP_BETWEEN_LEG_ODDS;
    }

    if (!isAllPodAreSameType && podType === PopularBetsPodTypes.SGP) {
        selectedPodHeights.GAP_BETWEEN_LEG = podHeightsConfig.Parlay.GAP_BETWEEN_LEG;
        selectedPodHeights.GAP_BETWEEN_LEG = showOptionPick
            ? selectedPodHeights.GAP_BETWEEN_LEG_ODDS
            : Math.max(podHeightsConfig.SGP.GAP_BETWEEN_LEG, podHeightsConfig.Parlay.GAP_BETWEEN_LEG);
        selectedPodHeights.LEG = showOptionPick ? selectedPodHeights.LEG_WITH_ODDS : Math.max(podHeightsConfig.SGP.LEG, podHeightsConfig.Parlay.LEG);
        selectedPodHeights.SUMMARY_TEXT = showOptionPick
            ? selectedPodHeights.SUMMARY_TEXT_WITH_ODDS
            : Math.max(podHeightsConfig.SGP.SUMMARY_TEXT, podHeightsConfig.Parlay.SUMMARY_TEXT);
    }

    if (!isAnyPodHasSummary) {
        selectedPodHeights.SUMMARY_TEXT = 0;
    }

    return selectedPodHeights;
}

function getMinPodHeight(config: IPodHeightConfigModel): number {
    const { HEADER, SUMMARY_TEXT, ADD_TO_BETSLIP } = getPodHeights(config);
    const minimumListHeight = getMinListHeight(config);

    return HEADER + SUMMARY_TEXT + minimumListHeight + ADD_TO_BETSLIP;
}

function getMinListHeight(config: IPodHeightConfigModel): number {
    const { rows } = config;
    const { GAP_BETWEEN_LEG, LEG } = getPodHeights(config);
    const totalGapHeight = GAP_BETWEEN_LEG * (rows - 1);

    return rows * LEG + totalGapHeight;
}

function getMinPodListWidth(numberOfPods: number): number {
    return numberOfPods ? DEFAULT_POD_WIDTH * numberOfPods + DEFAULT_POD_LIST_GAP * (numberOfPods - 1) : 0;
}

export { getMinListHeight, getMinPodHeight, getMinPodListWidth };
